/**
 * A switch, the way a switch looks: a track that fills and a knob that slides
 * over. Reads as on/off at a glance, which a checkbox never quite does for a
 * setting that changes how the whole shop works.
 *
 * It is a real <button role="switch">, so the keyboard and screen readers get
 * the same thing the eye does.
 */
export default function Toggle({ checked, onChange, disabled = false, label, hint, id }) {
  const control = (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={!!checked}
      aria-label={typeof label === 'string' ? label : undefined}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      // Off has to read as a switch, not as empty space: these settings sit on
      // sunken cards, so the track is a solid grey rather than the card colour.
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors duration-200 ${
        checked ? 'border-brand-600 bg-brand-600' : 'border-line-strong bg-line-strong'
      } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-4.5 w-4.5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
          checked ? 'translate-x-[1.45rem]' : 'translate-x-[0.2rem]'
        }`}
        style={{ height: '1.1rem', width: '1.1rem' }}
      />
    </button>
  );

  if (!label && !hint) return control;

  return (
    <div className="flex items-start gap-3">
      {control}
      <span className="min-w-0">
        {label && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => !disabled && onChange(!checked)}
            className={`block text-left text-sm font-semibold text-content ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          >
            {label}
          </button>
        )}
        {hint && <span className="block text-xs text-content-muted">{hint}</span>}
      </span>
    </div>
  );
}
